// 版本輪詢：偵測前端有無新部署，安全時自動重載，否則通知 UI 顯示提示。
//
// 行為（綜合 Slack / Notion 的折衷做法）：
//   - 切回前景 (visibilitychange → visible)：立即檢查
//   - 每 5 分鐘輪詢一次
//   - 偵測到新版本：
//       a. 如果安全（分頁隱藏 / 5 分鐘無互動 / focus 不在 input）→ 直接 reload
//       b. 否則：呼叫 listeners 顯示 snackbar，等使用者點按鈕
//       c. 使用者忽略 snackbar 超過 10 分鐘 → 進入「催促」模式（聽 listener 自行決定如何強硬處理）
//
// 服務端配合：build.json 由 scripts/write-build-info.js 寫入 public/。
// REACT_APP_BUILD_SHA-style 比對：build 時 baked 進 bundle 的 BUILD_SHA。

import { BUILD_SHA } from '../buildInfo';

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
const PROMPT_FORCE_AFTER_MS = 10 * 60 * 1000;

const listeners = new Set();
let started = false;
let latestSha = null;
let firstSeenNewVersionAt = null;
let lastInteractionAt = Date.now();

const trackInteraction = () => {
  lastInteractionAt = Date.now();
};

const isInputFocused = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
};

const isSafeToAutoReload = () => {
  if (document.visibilityState === 'hidden') return true;
  if (isInputFocused()) return false;
  return Date.now() - lastInteractionAt > IDLE_THRESHOLD_MS;
};

const fetchLatestSha = async () => {
  try {
    // 透過 outer nginx 的 alias 拿到 public frontend 的 build.json
    // （/build.json 會被 catch-all 路由錯地方）
    const res = await fetch(`/public-build.json?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.sha ? String(data.sha) : null;
  } catch (_) {
    return null;
  }
};

const notify = (state) => {
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (_) {
      /* ignore */
    }
  });
};

const tick = async () => {
  const sha = await fetchLatestSha();
  if (!sha) return;
  if (sha === BUILD_SHA) return;

  const isFirstSighting = latestSha !== sha;
  latestSha = sha;
  if (!firstSeenNewVersionAt) firstSeenNewVersionAt = Date.now();

  if (isSafeToAutoReload()) {
    window.location.reload();
    return;
  }

  const ignoredFor = Date.now() - firstSeenNewVersionAt;
  const escalated = ignoredFor > PROMPT_FORCE_AFTER_MS;

  if (isFirstSighting || escalated) {
    notify({
      currentSha: BUILD_SHA,
      latestSha: sha,
      escalated,
    });
  }
};

export const startVersionCheck = () => {
  if (started) return;
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') return;
  started = true;

  ['mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'].forEach((evt) =>
    window.addEventListener(evt, trackInteraction, { passive: true })
  );

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') tick();
  });

  setInterval(tick, POLL_INTERVAL_MS);
  setTimeout(tick, 30 * 1000);
};

export const subscribeVersionUpdate = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const reloadNow = () => {
  window.location.reload();
};
